
import { defineComponent, ref, watch, onMounted, computed } from "vue";

import Accordion from "../atoms/Accordion.vue";
import ComponentCard from "./ComponentCard.vue";
import { CommunityService } from "@/services/communities/CommunityServices";
import { BlockCommunity, CommunitySummary, Instructions, InstructionType} from "@/services/communities/types";

export default defineComponent({
  components: { Accordion, ComponentCard },
  setup() {
    const communityService = new CommunityService();

    const communities = ref<CommunitySummary[]>([]);
    const blocks = ref<BlockCommunity[]>([]);
    const selectedCommunityId = ref<number | null | undefined>(null);
    const selectedBlockId = ref<number | null | undefined>(null);

    const formattedInstructionPersonal = ref<
      { title: string; content: string }[]
    >([]);
    const formattedInstructionResident = ref<
      { title: string; content: string }[]
    >([]);

    const hasSelection = computed(
      () =>
        selectedCommunityId.value !== null && selectedBlockId.value !== null
    );

    const typeInstructions = [
      { value: 1, label: "Incendio" },
      { value: 2, label: "Terremoto" },
      { value: 3, label: "Tsunami" },
      { value: 4, label: "Evacuación" },
    ];

    const formatInstructions = (
      raw: Instructions[]
    ): { title: string; content: string }[] => {
      const grouped: Record<number, string[]> = {};
      raw.forEach((item) => {
        const emergencyId = item.instruction.emergencyTypeId;

        if (!grouped[emergencyId]) 
        {
          grouped[emergencyId] = [];
        }
        grouped[emergencyId].push(item.instruction.content);
      });

      return Object.entries(grouped).map(([emergencyIdStr, contents]) => {
        const emergencyId = Number(emergencyIdStr);
        const typeInfo = typeInstructions.find(
          (t) => t.value === emergencyId
        );
        const typeName = typeInfo ? `Instrucciones en caso de ${typeInfo.label}` : "Instrucciones";

        const content = contents
          .map(
            (content, index) =>
              `<li><strong>${index + 1}.</strong> ${content}</li>`
          )
          .join("");

        return {
          title: typeName,
          content: `<ol style="text-align: left;">${content}</ol>`,
        };
      });
    };

    const loadCommunities = async () => {
      const response = await communityService.getAdminCommunities();

      if (response && response.data) 
      {
        communities.value = response.data;

        if (communities.value.length > 0 && !selectedCommunityId.value) 
        {
          selectedCommunityId.value = communities.value[0].id;
        }
      }
    };

    const loadBlocks = async () => {
      if (!selectedCommunityId.value) return;

      const response = await communityService.getBlocksCommunity(
        selectedCommunityId.value
      );

      if (response && response.data) 
      {
        blocks.value = response.data;

        if (blocks.value.length > 0 && !selectedBlockId.value) 
        {
          selectedBlockId.value = blocks.value[0].id;
        }
      }
    };

    const loadInstructions = async () => {
      if (!hasSelection.value) return;
      const communityId = selectedCommunityId.value as number;
      const blockId = selectedBlockId.value as number;

      const personalType = new InstructionType("PERSONNEL");
      const residentType = new InstructionType("RESIDENT");

      const personalAll: Instructions[] = [];
      const residentAll: Instructions[] = [];

      for (let emergencyTypeId = 1; emergencyTypeId <= 4; emergencyTypeId++) 
      {
        const [personalRes, residentRes] = await Promise.all([
          communityService.getBlockIntructions(
            communityId,
            blockId,
            emergencyTypeId,
            personalType
          ),
          communityService.getBlockIntructions(
            communityId,
            blockId,
            emergencyTypeId,
            residentType
          ),
        ]);

        if (personalRes && personalRes.data) 
        {
          personalAll.push(...personalRes.data);
        }

        if (residentRes && residentRes.data) 
        {
          residentAll.push(...residentRes.data);
        }
      }

      formattedInstructionPersonal.value = formatInstructions(personalAll);
      formattedInstructionResident.value = formatInstructions(residentAll);
    };

    onMounted(async () => {
      await loadCommunities();
      await loadBlocks();
      await loadInstructions();
    });

    watch(selectedCommunityId, async () => {
      selectedBlockId.value = null;
      await loadBlocks();
      await loadInstructions();
    });

    watch(selectedBlockId, async () => {
      await loadInstructions();
    });

    return {
      communities,
      blocks,
      selectedCommunityId,
      selectedBlockId,
      formattedInstructionPersonal,
      formattedInstructionResident,
    };
  },
});
